import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { DEFAULT_TIME_ZONE, ianaTimezoneSchema } from '../timezone';

/**
 * Wire contract for previewing the Sona follow-up push.
 *
 * A non-production staff tool: a transcript goes in, and what the decision
 * Lambda would have decided for it comes out. Nothing is stored or sent, so
 * every engine result is a valid answer and arrives as a 200 with an
 * `outcome`, never as a server error.
 *
 * The request is `.strict()` and the response is not (ADR-135 Rule 1), the
 * same split as the bell's schemas beside this one. Slot and attempt values are
 * plain strings on the wire rather than the engine's enums: this package sits
 * below the personalisation package, and a reader that rejected a slot added
 * later would break on the next prompt change rather than show it.
 */

/** The longest transcript the route accepts, in characters. */
export const FOLLOW_UP_PREVIEW_TRANSCRIPT_MAX_CHARS = 120_000;

export const followUpPreviewRequestSchema = z
  .object({
    transcript: z
      .string()
      .max(FOLLOW_UP_PREVIEW_TRANSCRIPT_MAX_CHARS)
      .refine((value) => value.trim().length > 0, { message: 'transcript is empty' }),
    // The repo-wide default zone (ADR-095) when the caller names none.
    timeZone: ianaTimezoneSchema.default(DEFAULT_TIME_ZONE),
    now: z.iso.datetime({ offset: true }).optional(),
  })
  .strict();
export type FollowUpPreviewRequest = z.infer<typeof followUpPreviewRequestSchema>;

const followUpPreviewOfferSchema = z.object({
  /** The member-local clock the model was shown. */
  localNow: z.string(),
  slots: z.array(z.object({ slot: z.string(), localTime: z.string() })),
});

const followUpPreviewAttemptSchema = z.object({
  outcome: z.string(),
  turn: z.string().nullable(),
  latencyMs: z.number().nullable(),
  /** Null when the provider did not report usage. */
  inputTokens: z.number().nullable(),
  outputTokens: z.number().nullable(),
});

const commonFields = {
  promptVersion: z.string().nullable(),
  modelId: z.string(),
  offer: followUpPreviewOfferSchema,
  attempts: z.array(followUpPreviewAttemptSchema),
};

export const followUpPreviewDataSchema = z.discriminatedUnion('outcome', [
  z.object({
    ...commonFields,
    outcome: z.literal('push'),
    /** Actual runtime copy provenance; extensible string for older clients. */
    copySource: z.string().optional(),
    title: z.string(),
    body: z.string(),
    sendSlot: z.string(),
    /** The send time on the member's clock, as the offer shows times. */
    localSendTime: z.string(),
    /** Why the model's slot was corrected to the offer, or null when it was on it. */
    offMenu: z.string().nullable(),
  }),
  z.object({ ...commonFields, outcome: z.literal('declined'), reason: z.string() }),
  z.object({
    ...commonFields,
    outcome: z.literal('guard_rejected'),
    title: z.string(),
    body: z.string(),
    sendSlot: z.string(),
    rule: z.string().nullable(),
  }),
  z.object({
    ...commonFields,
    outcome: z.literal('generation_failed'),
    failureReason: z.string(),
  }),
]);
export type FollowUpPreviewData = z.infer<typeof followUpPreviewDataSchema>;
export type FollowUpPreviewOutcome = FollowUpPreviewData['outcome'];

export const followUpPreviewResponseSchema = apiEnvelope(followUpPreviewDataSchema);
export type FollowUpPreviewResponse = z.infer<typeof followUpPreviewResponseSchema>;
